import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, map } from 'rxjs';
import { AppSettings } from '../app-settings';
import { fabric } from 'fabric';

// Redondeo de esquinas y curvatura de aristas para fabric.Polygon (mismo patch que el front web,
// necesario para que las zonas del mapa se pinten en taquilla con la forma con la que se dibujaron).
// Se sobreescribe `commonRender` (que Polygon hereda de Polyline). Solo afecta al TRAZADO:
// el array `points` y el `pathOffset` no cambian, por lo que el hit-testing y la serialización
// siguen igual.
//
// Dos propiedades opcionales controlan el dibujo:
//  - `cornerRadius` (número): redondea TODAS las esquinas con ese radio (0 = rectas).
//  - `edgeCurvatures` (número[]): curvatura por arista i (vértice i → i+1). 0 = recta;
//    el signo abomba hacia un lado u otro. El punto de control de la Bézier cuadrática se
//    coloca en el punto medio de la arista desplazado perpendicularmente esa distancia.
//
// Ambas se combinan: cada esquina se recorta según el radio y el tramo entre esquinas se
// dibuja recto o curvado según la curvatura de esa arista. Con ambas a 0 replica el render
// recto original de fabric.
(function enableRoundedPolygonCorners() {
  const proto: any = (fabric as any).Polygon?.prototype;
  if (!proto || proto.__roundedCornersPatched) { return; }
  proto.__roundedCornersPatched = true;
  proto.commonRender = function (ctx: CanvasRenderingContext2D): boolean {
    const points = this.points;
    const len = points.length;
    const ox = this.pathOffset.x;
    const oy = this.pathOffset.y;
    const radius = this.cornerRadius || 0;
    const curv: number[] | null = Array.isArray(this.edgeCurvatures) ? this.edgeCurvatures : null;

    if (!len || isNaN(points[len - 1].y)) {
      return false;
    }

    const hasCurve = !!(curv && curv.some((c: number) => c));

    // Sin redondeo ni curvatura (o polígono degenerado): render recto idéntico al original.
    if ((!radius || len < 3) && !hasCurve) {
      ctx.beginPath();
      ctx.moveTo(points[0].x - ox, points[0].y - oy);
      for (let i = 0; i < len; i++) {
        ctx.lineTo(points[i].x - ox, points[i].y - oy);
      }
      return true;
    }

    // Puntos de recorte de cada esquina (enter = sobre la arista entrante, exit = sobre la
    // saliente). Con radius 0 coinciden con el propio vértice.
    const enter: { x: number, y: number }[] = new Array(len);
    const exit: { x: number, y: number }[] = new Array(len);
    for (let i = 0; i < len; i++) {
      const prev = points[(i - 1 + len) % len];
      const cur = points[i];
      const next = points[(i + 1) % len];
      const vinx = prev.x - cur.x, viny = prev.y - cur.y;
      const voutx = next.x - cur.x, vouty = next.y - cur.y;
      const lin = Math.sqrt(vinx * vinx + viny * viny) || 1;
      const lout = Math.sqrt(voutx * voutx + vouty * vouty) || 1;
      // Limitar el radio a la mitad de cada arista adyacente para que no se solape.
      const r = radius > 0 ? Math.min(radius, lin / 2, lout / 2) : 0;
      enter[i] = { x: cur.x + (vinx / lin) * r, y: cur.y + (viny / lin) * r };
      exit[i] = { x: cur.x + (voutx / lout) * r, y: cur.y + (vouty / lout) * r };
    }

    ctx.beginPath();
    ctx.moveTo(exit[0].x - ox, exit[0].y - oy);
    for (let i = 0; i < len; i++) {
      const j = (i + 1) % len;
      // Arista i: del punto de salida de la esquina i al de entrada de la esquina j.
      const c = curv ? (curv[i] || 0) : 0;
      if (c) {
        const a = points[i], b = points[j];
        const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
        const dx = b.x - a.x, dy = b.y - a.y;
        const l = Math.sqrt(dx * dx + dy * dy) || 1;
        const px = -dy / l, py = dx / l;
        const cxp = mx + px * c, cyp = my + py * c;
        ctx.quadraticCurveTo(cxp - ox, cyp - oy, enter[j].x - ox, enter[j].y - oy);
      } else {
        ctx.lineTo(enter[j].x - ox, enter[j].y - oy);
      }
      // Esquina en el vértice j: arco del punto de entrada al de salida (control = vértice j).
      if (radius > 0) {
        ctx.quadraticCurveTo(points[j].x - ox, points[j].y - oy, exit[j].x - ox, exit[j].y - oy);
      }
    }
    ctx.closePath();
    return true;
  };
})();

@Injectable({
  providedIn: 'root'
})
export class GenerateMapService {

  private colorDefaultSeat: string = '#ffffff';
  private colorDefaultFree: string = '#d8e1d7';
  private colorDefaultStage: string = '#bae4ff';
  private colorDefaultHidden: string = '#4d4d4d';
  private colorDefaultReserved: string = '#7c4dff';
  private colorSelectMap: string = '#FF4D80';

  // Número de elementos a partir del cual un SVG se considera demasiado complejo para renderizarse
  // sin caché en cada frame (ver addSvgObject).
  private readonly SVG_UNCACHED_ELEMENTS_LIMIT = 300;

  seatSections = new BehaviorSubject<any>([]);
  currentSeatSections = this.seatSections.asObservable();

  objectsMap = new BehaviorSubject<any>([]);
  currentObjectsMap = this.objectsMap.asObservable();

  levelsMap = new BehaviorSubject<any>([])
  currentLevelsMap = this.levelsMap.asObservable();

  blockedSeats = new BehaviorSubject<any>([])
  currentBlockedSeats = this.blockedSeats.asObservable();

  constructor(private http: HttpClient) { }

  resetState() {
    this.seatSections.next({});
    this.objectsMap.next({});
    this.levelsMap.next([]);
    this.blockedSeats.next([]);
  }

  // Métodos para gestionar los seatSections en el servicio
  setSeatSections(sections: any) {
    this.seatSections.next(sections);
    localStorage.setItem('seatSection', JSON.stringify(sections));
  }

  getSeatSections() {
    return this.seatSections.getValue();
  }

  // Métodos para gestionar los objetos del mapa en el servicio
  setObjectsMap(objects: any) {
    this.objectsMap.next(objects);
    localStorage.setItem('canvasObjects', JSON.stringify(objects));
  }

  getObjectsMap() {
    return this.objectsMap.getValue();
  }

  // Métodos para gestionar los niveles del mapa en el servicio
  setLevelsMap(levels: any) {
    this.levelsMap.next(levels);
    localStorage.setItem('seatLevels', JSON.stringify(levels));
  }

  getLevelsMap() {
    return this.levelsMap.getValue();
  }

  // Métodos para gestionar los asientos bloqueados en el servicio
  setBlockedSeats(seats: any) {
    this.blockedSeats.next(seats);
    localStorage.setItem('blockedSeats', JSON.stringify(seats));
  }

  getBlockedSeats() {
    return this.blockedSeats.getValue();
  }

  // Método para agregar un grupo de asientos
  addGroupSeats(rowsGroup: number = 15, columnsGroup: number = 12, sectionNumber: number, sectionData?: any, fromEvent?: boolean, bookedSeats?: any[], blockedSeats?: any[], cartBookings?: any[], fromEdit?: boolean, reservedSeats?: any[]): { mainGroup: any, sectionObject: any } {
    let seatEnabled = 0;
    let config = {
      seatsGroupConfig: {
        rowsLegendLeft: true,
        rowsLegendRight: true
      },
      originX: 'center',
      originY: 'center',
      left: 0,
      top: 0,
      scaleX: 1,
      scaleY: 1,
      angle: 0
    };
    let sectionId = sectionNumber;
    let sectionName = 'Sección ' + sectionNumber;
    const filas = rowsGroup;
    const columns = columnsGroup;
    var top = 10;
    var left = 0;
    var arrayElements = [];
    let seats = [];
    let titleConfiguration: any = {
      fontSize: 18,
      fontFamily: 'Satoshi-Regular',
      left: 0,
      top: 0,
      originX: 'center',
      originY: 'top',
      eventSeat: false,
      textAlign: 'center',
      visible: true,
    };

    if (sectionData) {
      const { section, elements } = sectionData;
      config = section.configuration;
      sectionId = section.id;
      sectionName = section.name;
      top = section.configuration.seatsGroupConfig.top;
      titleConfiguration = section.configuration.titleConfiguration;
      // Sanitize legacy value
      if (titleConfiguration.left === 'left') titleConfiguration.left = 0;
    }

    // Build a lookup map keyed by "row_column_sectionId" for O(1) access inside the nested loop
    const elementsMap = new Map<string, any>();
    if (sectionData) {
      const { section, elements } = sectionData;
      for (const el of elements) {
        elementsMap.set(`${el.row}_${el.column}_${el.section}`, el);
      }
    }
    const text = new fabric.Text(sectionName, titleConfiguration);
    const numberAngle: number = (config.seatsGroupConfig as any)?.numberAngle || 0;
    const seatNumberAngle: number = (config.seatsGroupConfig as any)?.seatNumberAngle || 0;
    const colSpacing: number = (config.seatsGroupConfig as any)?.columnSpacing || 1.2;
    const rowSpacing: number = (config.seatsGroupConfig as any)?.rowSpacing || 1.2;
    const curvature: number = (config.seatsGroupConfig as any)?.curvature || 0;
    const midCol = (columns - 1) / 2;

    // ==== Curvatura RADIAL / ELÍPTICA (arcos concéntricos alrededor de un centro externo propio) ====
    // Mismo layout que el front web: modo opt-in por sección. Las butacas se colocan sobre arcos
    // concéntricos en torno a un punto C (coords de canvas), en vez de la parábola local por
    // sección ('parabolic', por defecto y retrocompatible). 'radial' es siempre círculo perfecto;
    // 'elliptical' es el mismo layout pero permitiendo achatar (ellipseAspect) y rotar
    // (ellipseRotation) el óvalo.
    const sgc: any = config.seatsGroupConfig || {};

    // Espaciado de filas por fila (solo modo 'parabolic'): cada fila puede tener su propio
    // hueco respecto a la fila anterior, en vez de que todas usen rowSpacing global. Se indexa
    // por posición física 1-based (clave N = espacio entre la fila N-1 y la fila N; la fila 1
    // no tiene "fila anterior" así que su clave nunca se usa). rowOffsets[numF] = desplazamiento
    // acumulado desde la fila 0 hasta la fila numF; sin overrides, equivale a numF * rowSpacing.
    const rowSpacingOverrides: Record<number, number> = sgc.rowSpacingPerRowEnabled && sgc.rowSpacingOverrides ? sgc.rowSpacingOverrides : {};
    const rowOffsets: number[] = [];
    {
      let acc = 0;
      for (let numF = 0; numF < filas; numF++) {
        rowOffsets[numF] = acc;
        const nextPosition = numF + 2;
        const override = rowSpacingOverrides[nextPosition];
        acc += (override != null ? +override : rowSpacing);
      }
    }

    // Espaciado entre butacas por fila (solo modo 'parabolic'): cada fila puede tener su propio
    // hueco horizontal entre butacas, en vez de que todas usen columnSpacing global. Se indexa
    // por posición física 1-based (clave N = espacio entre butacas de la fila N).
    const columnSpacingOverrides: Record<number, number> = sgc.rowSpacingPerRowEnabled && sgc.columnSpacingOverrides ? sgc.columnSpacingOverrides : {};
    const getRowColSpacing = (numF: number): number => {
      const override = columnSpacingOverrides[numF + 1];
      return override != null ? +override : colSpacing;
    };

    // Desplazamiento horizontal manual por fila (solo modo 'parabolic'): al ensanchar el espacio
    // entre butacas de una fila, esa fila crece siempre desde el mismo lado, quedando desalineada
    // del resto; este offset (mismas unidades que colSpacing) la recoloca.
    const rowOffsetXOverrides: Record<number, number> = sgc.rowSpacingPerRowEnabled && sgc.rowOffsetXOverrides ? sgc.rowOffsetXOverrides : {};
    const getRowOffsetX = (numF: number): number => {
      const override = rowOffsetXOverrides[numF + 1];
      return override != null ? +override : 0;
    };

    const curvatureMode: string = sgc.curvatureMode || 'parabolic';
    const isElliptical: boolean = curvatureMode === 'elliptical';
    const isRadial: boolean = (curvatureMode === 'radial' || isElliptical) && !!sgc.curvatureCenter;
    const curvatureCenter = sgc.curvatureCenter || null;
    const radialInvert: boolean = !!sgc.radialInvert;
    // El radial es siempre círculo perfecto (aspect=1, rotación=0); solo el elíptico usa los
    // valores guardados, para no arrastrar un achatamiento/rotación obsoleto si la sección
    // cambió alguna vez de modo.
    const ellipseAspect: number = isElliptical ? (sgc.ellipseAspect != null ? +sgc.ellipseAspect : 1) : 1;
    const ellipseRotationDeg: number = isElliptical ? (+sgc.ellipseRotation || 0) : 0;
    // Escala neta local(seatsGroup)→canvas = escala del mainGroup × escala del seatsGroup.
    const netScale: number = ((config.scaleX ?? 1) || 1) * ((sgc.scaleX ?? 1) || 1);
    const midRow = (filas - 1) / 2;
    // Las butacas parabólicas se posicionan con originX/originY NUMÉRICO (origen normalizado de
    // fabric): 1 unidad ≈ el ancho del asiento. En radial, en cambio, se colocan con left/top
    // REALES y originX/originY:'center'. Para igualar la densidad visual, el paso se pasa a espacio
    // hijo multiplicando por el tamaño del asiento (SEAT_SIZE), y el radio dividiendo canvas/netScale.
    const SEAT_SIZE = 100; // diámetro base del círculo de butaca (radius 50) en espacio hijo.
    let R0child = 0;
    let radialAngleDeg = 0;
    let phi = 0;
    const colStep = colSpacing * SEAT_SIZE;
    const rowStep = rowSpacing * SEAT_SIZE;
    if (isRadial) {
      const cx = curvatureCenter.x, cy = curvatureCenter.y;
      // La sección NUNCA se traslada para "engancharse" a C: su posición (config.left/top) es la
      // que tiene guardada. C solo se usa como referencia para calcular la distancia y el ángulo
      // hacia el centro: eso determina el radio del arco y hacia dónde "mira" la sección.
      // Radio: si hay un valor explícito guardado se respeta; si no, se deriva de la distancia
      // sección↔C.
      const Dcanvas = (sgc.baseRadius != null && +sgc.baseRadius > 0)
        ? +sgc.baseRadius
        : Math.hypot((config.left ?? 0) - cx, (config.top ?? 0) - cy);
      // Radio en espacio hijo (seatsGroup). Guarda: mínimo para evitar el "abanico" degenerado.
      const minChildRadius = Math.max(rowStep * (filas + 2), colStep * 2);
      R0child = Math.max(Dcanvas / (netScale || 1), minChildRadius);
      // φ = dirección de C→sección; el grupo se orienta para "mirar" al centro.
      phi = Math.atan2((config.top ?? 0) - cy, (config.left ?? 0) - cx);
      radialAngleDeg = (phi * 180 / Math.PI) + 90;
    }
    // Ajuste manual que se superpuso rotando la sección a mano con el tirador en el editor. Se suma
    // al ángulo final del grupo, pero NO participa en `groupAngleRad` (usado para calcular la
    // posición local de las butacas): así la geometría del arco sigue anclada a la auto-orientación
    // hacia C, y el offset solo gira visualmente la sección entera (texto + butacas).
    const manualAngleOffsetDeg: number = isRadial ? (+sgc.manualAngleOffsetDeg || 0) : 0;
    // Paso angular constante (columnas radiales). El centro de curvatura queda en el LOCAL (0,0)
    // del seatsGroup; las butacas se colocan "encima" (y negativo) a distancia R_fila.
    const dTheta = R0child ? (colStep / R0child) : 0;
    const ellipseRotationRad = ellipseRotationDeg * Math.PI / 180;
    const groupAngleRad = radialAngleDeg * Math.PI / 180;
    // Deshace la rotación propia del grupo (siempre orientado a "mirar" a C) para poder trabajar
    // en un sistema GLOBAL común a todas las secciones que comparten centro C.
    const cosNegG = Math.cos(-groupAngleRad), sinNegG = Math.sin(-groupAngleRad);
    const cosER = Math.cos(ellipseRotationRad), sinER = Math.sin(ellipseRotationRad);
    // La posición de cada butaca se calcula sobre UNA ÚNICA elipse global (centrada en C,
    // semiejes rx=Rr/ry=Rr·ellipseAspect, rotada ellipseRotationDeg respecto al eje X del canvas),
    // igual para todas las secciones que apuntan al mismo C, sin importar su propio ángulo φ.
    // Con ellipseAspect=1 y rotación=0 el resultado es el círculo perfecto del modo 'radial'.
    const radialPos = (rowIdx: number, col: number): { x: number, y: number } => {
      const rEff = radialInvert ? (filas - 1 - rowIdx) : rowIdx;
      const Rr = R0child + (rEff - midRow) * rowStep;
      const alpha = (col - midCol) * dTheta;
      // θ = ángulo GLOBAL (bearing) de esta butaca respecto a C (phi - alpha conserva el mismo
      // sentido de columnas que el layout plano original: col creciente → izquierda).
      const theta = phi - alpha;
      const rxRow = Rr;
      const ryRow = Rr * ellipseAspect;
      const phiLocal = theta - ellipseRotationRad;
      const cosL = Math.cos(phiLocal), sinL = Math.sin(phiLocal);
      const rAt = (rxRow * ryRow) / Math.sqrt((ryRow * cosL) ** 2 + (rxRow * sinL) ** 2);
      const xE = rAt * cosL, yE = rAt * sinL;
      const worldX = xE * cosER - yE * sinER;
      const worldY = xE * sinER + yE * cosER;
      // Vuelve a expresar el punto de la elipse global en el marco LOCAL de esta sección
      // (deshaciendo su rotación propia), que es lo que espera fabric para el hijo del grupo.
      return {
        x: worldX * cosNegG - worldY * sinNegG,
        y: worldX * sinNegG + worldY * cosNegG,
      };
    };
    // Coloca una butaca/leyenda radial con posición REAL (left/top) y origen centrado.
    const applyRadial = (el: any, rowIdx: number, col: number): { x: number, y: number } => {
      const p = radialPos(rowIdx, col);
      el.set({ originX: 'center', originY: 'center', left: p.x, top: p.y });
      return p;
    };

    let aux = 1;

    // Butacas
    for (let numF = 0; numF < filas; numF++) {
      if (sectionData) {
        const { section, elements } = sectionData;
        aux = elements[numF * columns]?.row || aux;
        if (numF == 0) {
          top = top + (elements[numF * columns]?.configuration.top || 0);
        }
      }

      const rowColSpacing = getRowColSpacing(numF);
      const rowOffsetX = getRowOffsetX(numF);
      const baseOriginYLeft = top - rowOffsets[numF];
      const distLeft = (-1 - midCol) / (midCol || 1);
      const curveOffsetLeft = curvature * distLeft * distLeft * 0.5;

      const file = this.generateSeatObject((aux).toString(), false, numberAngle);
      if (isRadial) {
        applyRadial(file, numF, -1);
      } else {
        file.originX = left + rowOffsetX;
        file.originY = baseOriginYLeft + curveOffsetLeft;
        if (curvature) {
          file._baseOriginY = baseOriginYLeft;
        }
      }
      file.column = 1;
      file.isVisible = config.seatsGroupConfig.rowsLegendLeft;
      file.visible = config.seatsGroupConfig.rowsLegendLeft;
      file.eventSeat = false;
      file.numberLeft = true;
      arrayElements.push(file);
      for (let numC = 0; numC < columns; numC++) {
        let isVisible = true;
        let hidden = false;
        let colorSeat = this.colorDefaultFree;
        let colorSpecial = null;
        let id = null;
        let seat = (numC + 1);
        let baseOriginY = top - rowOffsets[numF];
        const distFromCenter = (numC - midCol) / (midCol || 1);
        const curveOffset = curvature * distFromCenter * distFromCenter * 0.5;
        let originY = baseOriginY + curveOffset;

        if (sectionData) {
          const { section, elements } = sectionData;
          let saveElement = elements.find((el: any) => el.row === aux && el.column === (numC + 1) && (+el.section === +section.id || (section.id_bbdd != null && +el.section === +section.id_bbdd)));
          colorSeat = saveElement?.configuration.special_color || section.color;
          colorSpecial = saveElement?.configuration.special_color || null;
          isVisible = saveElement ? (saveElement.configuration.isVisible ?? true) : false;
          id = saveElement?.id || null;
          seat = saveElement?.seat || (numC + 1);
        }

        let element = this.generateSeatObject(seat.toString(), true, seatNumberAngle);
        element.id = id;
        let radialXY: { x: number, y: number } | null = null;
        if (isRadial) {
          radialXY = applyRadial(element, numF, numC);
        } else {
          element.originX = left + rowOffsetX - rowColSpacing - numC * rowColSpacing;
          element.originY = originY;
          if (curvature) {
            element._baseOriginY = baseOriginY;
          }
        }
        element.eventSeat = true;
        element.row = aux;
        element.seat = seat;
        element.column = numC + 1;
        element.isVisible = isVisible;
        element.visible = isVisible;
        element.hidden = hidden;
        element.section = { id: sectionId, name: sectionName };
        element.color = colorSeat;
        element.colorSpecial = colorSpecial;

        // Comprobar si existe dentro de asientos bloqueados y si es así, poner otro color
        if (fromEvent) {
          element.eventSeat = element.eventSeat && element.isVisible;
          let exist = bookedSeats?.includes(element.id);
          let existBlocked = blockedSeats?.find(blocked => blocked.id_seats?.includes(element.id));
          let existReserved = reservedSeats?.includes(element.id);
          let existInCart = cartBookings?.includes(element.id);
          if (exist || existBlocked || existReserved) {
            element.hidden = true;
          }
          if (element.eventSeat) {
            element.hoverCursor = 'pointer';
            element.selectable = true;
            seatEnabled += 1;
          }
          if (element.hidden) {
            element.hoverCursor = 'default';
            element.selectable = false;
            colorSeat = existInCart ? this.colorSelectMap : (exist ? this.colorDefaultHidden : (existReserved ? this.colorDefaultReserved : (existBlocked ? existBlocked.color : this.colorDefaultHidden)));
            if (element.eventSeat) {
              seatEnabled -= 1;
            }
          }
        }

        if (sectionData) {
          element = this.changeSeatColor(element, colorSeat);
        }
        arrayElements.push(element);

        let objectSeat = {
          id: null,
          seat: seat,
          row: aux,
          column: numC + 1,
          section: sectionId,
          configuration: {
            originX: isRadial ? (radialXY ? radialXY.x : 0) : (left + rowOffsetX - rowColSpacing - numC * rowColSpacing),
            originY: isRadial ? (radialXY ? radialXY.y : 0) : originY,
            // En radial se guarda el modo de posicionamiento: left/top reales con origen centrado
            // (el layout se recomputa del modelo al recargar; esto es solo referencia/fallback).
            radialCenteredOrigin: isRadial ? true : undefined,
            _baseOriginY: (!isRadial && curvature) ? baseOriginY : undefined,
            eventSeat: true,
            isVisible: isVisible,
            section: { id: sectionId, name: sectionName },
            hidden: hidden,
            color: colorSeat
          }
        }
        seats.push(objectSeat);
      }
      const endFile = this.generateSeatObject((aux).toString(), false, numberAngle);
      if (isRadial) {
        applyRadial(endFile, numF, columns);
      } else {
        endFile.originX = left + rowOffsetX - rowColSpacing - columns * rowColSpacing;
        const baseOriginYRight = top - rowOffsets[numF];
        const distRight = (columns - midCol) / (midCol || 1);
        const curveOffsetRight = curvature * distRight * distRight * 0.5;
        endFile.originY = baseOriginYRight + curveOffsetRight;
        if (curvature) {
          endFile._baseOriginY = baseOriginYRight;
        }
      }
      endFile.column = columns;
      endFile.isVisible = config.seatsGroupConfig.rowsLegendRight;
      endFile.visible = config.seatsGroupConfig.rowsLegendRight;
      endFile.numberRight = true;
      endFile.eventSeat = false;
      arrayElements.push(endFile);
      aux++;
    }

    // Grupo de butacas
    let sectionConfiguration = {
      left: 0,
      top: 30,
      scaleX: 0.4,
      scaleY: 0.4,
      hasControls: false,
      arraySeats: true,
      eventSeat: false,
      originX: 'center',
      rows: filas,
      columns: columns,
      showRowsTickets: true,
      rowsLegendLeft: true,
      rowsLegendRight: true,
      section: { id: sectionId, name: sectionName },
    };
    if (sectionData) {
      const { section, elements } = sectionData;
      sectionConfiguration = section.configuration.seatsGroupConfig;
    }
    var seatsGroup = new fabric.Group(arrayElements, sectionConfiguration);
    if (fromEvent || fromEdit) {
      seatsGroup.subTargetCheck = true;
      text.hoverCursor = 'default';
      text.selectable = false;
    }
    if (fromEdit) {
      seatsGroup.set({
        lockMovementX: false,
        lockMovementY: false,
        lockRotation: false,
        lockScalingFlip: false,
        lockScalingY: false,
        lockScalingX: false,
        hasControls: true,
        selectable: true,
        evented: true,
        hasBorders: true,
        hoverCursor: 'move',
      });
    }

    const sectionLevel = sectionData?.section?.level != null ? +sectionData.section.level : null;

    var mainGroup = new fabric.Group([text, seatsGroup], {
      groupSeat: true,
      hasControls: fromEdit ? true : false,
      selectable: fromEdit ? true : false,
      evented: true,
      eventSeat: false,
      originX: config.originX || 'center',
      originY: config.originY || 'center',
      left: config.left,
      top: config.top,
      scaleX: config.scaleX,
      scaleY: config.scaleY,
      // En radial/elíptico el ángulo se recalcula (la sección "mira" al centro de curvatura) y se
      // le suma el ajuste manual guardado; en parabólico se usa el ángulo tal cual.
      angle: isRadial ? (radialAngleDeg + manualAngleOffsetDeg) : (config.angle || 0),
      subTargetCheck: true,
      perPixelTargetFind: true,
      lockMovementY: fromEdit ? false : true,
      lockMovementX: fromEdit ? false : true,
      lockRotation: fromEdit ? false : true,
      lockScalingFlip: fromEdit ? false : true,
      lockScalingY: fromEdit ? false : true,
      lockScalingX: fromEdit ? false : true,
      hoverCursor: fromEdit ? 'move' : 'pointer',
      hasBorders: fromEdit ? true : false,
      area: sectionLevel,
    });

    if (fromEvent) {
      mainGroup.subTargetCheck = true;
      mainGroup.perPixelTargetFind = true;
      mainGroup.lockMovementY = true;
      mainGroup.lockMovementX = true;
      mainGroup.lockRotation = true;
      mainGroup.lockScalingFlip = true;
      mainGroup.lockScalingY = true;
      mainGroup.lockScalingX = true;
      mainGroup.hoverCursor = 'pointer';
      mainGroup.hasBorders = false;
    }

    const mainGroupConfiguration = {
      left: mainGroup.left,
      top: mainGroup.top,
      scaleX: mainGroup.scaleX,
      scaleY: mainGroup.scaleY,
      angle: mainGroup.angle || 0,
      originX: mainGroup.originX,
      originY: mainGroup.originY,
      seatsGroupConfig: sectionConfiguration,
      titleConfiguration: titleConfiguration
    };

    const sectionObject = {
      section: {
        id: sectionData ? sectionId : sectionNumber,
        name: sectionName,
        color: sectionData?.section?.color || this.colorDefaultSeat,
        columns: columns,
        rows: filas,
        level: sectionLevel,
        configuration: mainGroupConfiguration,
      },
      elements: seats
    }
    return { mainGroup: mainGroup, sectionObject: sectionObject };
  }

  addGroupNumbers(rowsGroup: number, columnsGroup: number, sectionData?: any, fromEdit?: boolean): any {
    let config = {
      originX: 'center',
      originY: 'center',
      left: 0,
      top: 0,
      scaleX: 1,
      scaleY: 1,
      angle: 0
    };
    const filas = rowsGroup;
    const columns = columnsGroup;
    var top = 10;
    var left = 0;
    var arrayElements = [];
    let elementsText = sectionData?.elementsText || [];

    if (sectionData) {
      config = sectionData;
      top = sectionData.top;
    }

    let aux = 1;

    // Butacas
    for (let numF = 0; numF < filas; numF++) {
      if (elementsText && elementsText.length > 0) {
        let seatText = elementsText.find((el: any) => el.row === numF + 1 && el.column === 1);
        aux = seatText ? seatText.text : aux;
      }
      const file = this.generateSeatObject((aux).toString(), false);
      file.originX = left;
      file.originY = top - numF * 1.2;
      file.column = 1;
      file.eventSeat = false;
      file.numberLeft = true;
      file.hoverCursor = 'default';
      arrayElements.push(file);
      aux++;
    }

    // Grupo de butacas
    let sectionConfiguration = {
      left: 0,
      top: 30,
      scaleX: 0.4,
      scaleY: 0.4,
      hasControls: false,
      arraySeats: false,
      eventSeat: false,
      originX: 'center',
      rows: filas,
      columns: columns,
      hoverCursor: 'default',
      selectable: false
    };
    var seatsGroup = new fabric.Group(arrayElements, sectionConfiguration);

    var mainGroup = new fabric.Group([seatsGroup], {
      groupSeat: false,
      hasControls: false,
      eventSeat: false,
      originX: config.originX || 'center',
      originY: config.originY || 'center',
      left: config.left,
      top: config.top,
      scaleX: config.scaleX,
      scaleY: config.scaleY,
      angle: config.angle || 0,
      subTargetCheck: true,
      perPixelTargetFind: true,
      lockMovementY: true,
      lockMovementX: true,
      lockRotation: true,
      lockScalingFlip: true,
      lockScalingY: true,
      lockScalingX: true,
      hoverCursor: 'pointer',
      hasBorders: false
    });

    if (fromEdit) {
      mainGroup.set({
        selectable: true,
      });
    }

    return mainGroup;
  }

  // Método para agregar un objeto asiento
  generateSeatObject(seatNumber: any, fill: boolean = true, numberAngle: number = 0) {
    const circle = new fabric.Circle({
      radius: 50,
      fill: 'transparent',
      strokeWidth: 2,
      originX: 'center',
      originY: 'center',
    });
    var color = 'black';
    if (fill) {
      circle.fill = this.colorDefaultSeat;
      circle.stroke = '#4D4D4D';
      color = 'black';
    }

    var text = this.createTextComponent(seatNumber.toString(), 40, 0, color);
    if (numberAngle) {
      text.set('angle', numberAngle);
    }

    var group = new fabric.Group([circle, text], {
      hasControls: false,
      radius: 50,
    });
    return group;
  }

  // Método para agregar un objeto poligonal, fondo para las secciones: Patio de butacas, 1º Anfiteatro, 2º Anfiteatro, etc.
  addPoligonObject(config?: any) {
    // Trapecio simétrico por defecto: base inferior más ancha que la superior
    var points = [
      { x: 40,  y: 0   },
      { x: 260, y: 0   },
      { x: 300, y: 180 },
      { x: 0,   y: 180 }
    ];
    points = config?.points || points;
    let polygonConfig = {
      left: 100,
      top: 50,
      fill: this.colorDefaultFree,
      oldFill: this.colorDefaultFree,
      strokeWidth: 1,
      stroke: '#a9a8b3',
      scaleX: 1,
      scaleY: 1,
      angle: 0,
      objectCaching: false,
      transparentCorners: true,
      isInfoSection: true,
      originX: 'center',
      originY: 'center',
      width: 700,
      height: 250,
      eventSeat: false,
      perPixelTargetFind: true,
      objectId: null,
      level: null,
      // Radio de redondeo de todas las esquinas (0 = esquinas rectas).
      cornerRadius: 0,
      // Curvatura por arista i (vértice i → i+1); 0 = recta. undefined = todas rectas.
      edgeCurvatures: undefined as (number[] | undefined)
    }

    if (config) {
      polygonConfig.left = config.left || polygonConfig.left;
      polygonConfig.cornerRadius = config.cornerRadius != null ? config.cornerRadius : polygonConfig.cornerRadius;
      polygonConfig.edgeCurvatures = Array.isArray(config.edgeCurvatures) ? [...config.edgeCurvatures] : polygonConfig.edgeCurvatures;
      polygonConfig.top = config.top || polygonConfig.top;
      if (config.fill) {
        polygonConfig.fill = config.fill;
        polygonConfig.oldFill = config.fill;
      }
      // Restaurar grosor del borde (permite 0 = sin borde) para que se repinte igual que en la web.
      polygonConfig.strokeWidth = config.strokeWidth != null ? config.strokeWidth : polygonConfig.strokeWidth;
      polygonConfig.stroke = config.stroke || polygonConfig.stroke;
      polygonConfig.scaleX = config.scaleX || polygonConfig.scaleX;
      polygonConfig.scaleY = config.scaleY || polygonConfig.scaleY;
      polygonConfig.angle = config.angle || polygonConfig.angle;
      polygonConfig.width = config.width || polygonConfig.width;
      polygonConfig.height = config.height || polygonConfig.height;
      polygonConfig.objectId = config.objectId || null;
      // Normalize level: can be an integer ID or a full level object (legacy format)
      const configLevel = config.level;
      polygonConfig.level = (configLevel != null && typeof configLevel === 'object')
        ? (configLevel.id ?? null)
        : (configLevel || null);
    }

    var polygon = new fabric.Polygon(points, polygonConfig);
    return polygon;
  }

  // Crear texto
  createTextComponent(text: any, fontsize: number, top: number = 0, color: string = 'black') {
    let textComponent = new fabric.Text(text, {
      fontFamily: 'Satoshi-Regular',
      fontSize: fontsize,
      fill: color,
      textAlign: 'center',
      originX: 'center',
      originY: 'center',
      eventSeat: false,
      top: top
    });
    return textComponent;
  }

  // Crear rectángulo
  createRect(color: string, width: number, height: number) {
    const rectangle = new fabric.Rect({
      fill: color,
      stroke: '#a9a8b3',
      strokeWidth: 0.5,
      originX: 'center',
      originY: 'center',
      width: width,
      height: height,
      eventSeat: false
    });

    return rectangle;
  }

  // Método para agregar un rectángulo como sección de fondo (equivalente a addPoligonObject pero para rectángulos)
  addRectObject(config?: any) {
    let rectConfig: any = {
      left: 100,
      top: 50,
      fill: this.colorDefaultFree,
      oldFill: this.colorDefaultFree,
      strokeWidth: 1,
      stroke: '#a9a8b3',
      scaleX: 1,
      scaleY: 1,
      angle: 0,
      objectCaching: false,
      transparentCorners: true,
      isInfoSection: true,
      originX: 'center',
      originY: 'center',
      width: 700,
      height: 250,
      eventSeat: false,
      perPixelTargetFind: true,
      objectId: null,
      level: null
    };

    if (config) {
      rectConfig.left = config.left || rectConfig.left;
      rectConfig.top = config.top || rectConfig.top;
      if (config.fill) {
        rectConfig.fill = config.fill;
        rectConfig.oldFill = config.fill;
      }
      rectConfig.stroke = config.stroke || rectConfig.stroke;
      rectConfig.strokeWidth = config.strokeWidth != null ? config.strokeWidth : rectConfig.strokeWidth;
      rectConfig.scaleX = config.scaleX || rectConfig.scaleX;
      rectConfig.scaleY = config.scaleY || rectConfig.scaleY;
      rectConfig.angle = config.angle || rectConfig.angle;
      rectConfig.width = config.width || rectConfig.width;
      rectConfig.height = config.height || rectConfig.height;
      rectConfig.objectId = config.objectId || null;
      // Normalize level: can be an integer ID or a full level object (legacy format)
      const rectConfigLevel = config.level;
      rectConfig.level = (rectConfigLevel != null && typeof rectConfigLevel === 'object')
        ? (rectConfigLevel.id ?? null)
        : (rectConfigLevel || null);
    }

    return new fabric.Rect(rectConfig);
  }

  // Método para agregar un círculo/elipse como sección de fondo (equivalente a addRectObject pero para elipses)
  addEllipseObject(config?: any) {
    let ellipseConfig: any = {
      left: 100,
      top: 50,
      fill: this.colorDefaultFree,
      oldFill: this.colorDefaultFree,
      strokeWidth: 1,
      stroke: '#a9a8b3',
      scaleX: 1,
      scaleY: 1,
      angle: 0,
      // Por defecto un círculo (rx === ry); en el editor el usuario puede estirarlo a elipse.
      rx: 150,
      ry: 150,
      objectCaching: false,
      transparentCorners: true,
      isInfoSection: true,
      originX: 'center',
      originY: 'center',
      eventSeat: false,
      perPixelTargetFind: true,
      objectId: null,
      level: null
    };

    if (config) {
      ellipseConfig.left = config.left || ellipseConfig.left;
      ellipseConfig.top = config.top || ellipseConfig.top;
      if (config.fill) {
        ellipseConfig.fill = config.fill;
        ellipseConfig.oldFill = config.fill;
      }
      ellipseConfig.stroke = config.stroke || ellipseConfig.stroke;
      ellipseConfig.strokeWidth = config.strokeWidth != null ? config.strokeWidth : ellipseConfig.strokeWidth;
      ellipseConfig.scaleX = config.scaleX || ellipseConfig.scaleX;
      ellipseConfig.scaleY = config.scaleY || ellipseConfig.scaleY;
      ellipseConfig.angle = config.angle || ellipseConfig.angle;
      ellipseConfig.rx = config.rx || ellipseConfig.rx;
      ellipseConfig.ry = config.ry || ellipseConfig.ry;
      ellipseConfig.objectId = config.objectId || null;
      // Normalize level: can be an integer ID or a full level object (legacy format)
      const ellipseConfigLevel = config.level;
      ellipseConfig.level = (ellipseConfigLevel != null && typeof ellipseConfigLevel === 'object')
        ? (ellipseConfigLevel.id ?? null)
        : (ellipseConfigLevel != null ? ellipseConfigLevel : null);
    }

    return new fabric.Ellipse(ellipseConfig);
  }

  // Construye el path SVG de una línea como curva cuadrática de Bézier.
  // curvature = desplazamiento perpendicular (px) del punto de control respecto al punto medio.
  // curvature 0 => el punto de control cae en el medio => línea recta.
  buildLinePath(points: number[], curvature: number = 0): string {
    const [x1, y1, x2, y2] = points;
    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    // Vector perpendicular unitario.
    const px = -dy / len;
    const py = dx / len;
    const qx = mx + px * (curvature || 0);
    const qy = my + py * (curvature || 0);
    return `M ${x1} ${y1} Q ${qx} ${qy} ${x2} ${y2}`;
  }

  // Método para agregar una línea (recta o curva). A diferencia de los polígonos/rectángulos/elipses
  // la línea no tiene relleno: solo trazo (color y grosor). Se implementa como un path de Bézier para
  // poder darle curvatura; con curvature 0 se ve como una recta. Se posiciona por su centro para
  // ser coherente con el resto de formas.
  addLineObject(config?: any) {
    // Línea horizontal por defecto.
    const points = Array.isArray(config?.points) && config.points.length >= 4
      ? config.points
      : [0, 0, 300, 0];
    const curvature = config?.curvature || 0;
    let lineConfig: any = {
      left: 100,
      top: 50,
      stroke: '#000000',
      strokeWidth: 3,
      fill: '', // sin relleno: la línea es solo trazo
      // strokeUniform mantiene el grosor constante aunque se escale la línea.
      strokeUniform: true,
      scaleX: 1,
      scaleY: 1,
      angle: 0,
      objectCaching: false,
      transparentCorners: true,
      isInfoSection: true,
      // Flag propio para identificar la línea (su type de fabric es 'path').
      isLine: true,
      linePoints: points,
      curvature: curvature,
      originX: 'center',
      originY: 'center',
      eventSeat: false,
      // padding extra para poder seleccionar una línea fina con facilidad.
      padding: 6,
      objectId: null,
      level: null
    };

    if (config) {
      lineConfig.left = config.left || lineConfig.left;
      lineConfig.top = config.top || lineConfig.top;
      lineConfig.stroke = config.stroke || lineConfig.stroke;
      lineConfig.strokeWidth = config.strokeWidth != null ? config.strokeWidth : lineConfig.strokeWidth;
      lineConfig.scaleX = config.scaleX || lineConfig.scaleX;
      lineConfig.scaleY = config.scaleY || lineConfig.scaleY;
      lineConfig.angle = config.angle || lineConfig.angle;
      lineConfig.objectId = config.objectId || null;
      const lineConfigLevel = config.level;
      lineConfig.level = (lineConfigLevel != null && typeof lineConfigLevel === 'object')
        ? (lineConfigLevel.id ?? null)
        : (lineConfigLevel != null ? lineConfigLevel : null);
    }

    return new fabric.Path(this.buildLinePath(points, curvature), lineConfig);
  }

  // Método para recrear una imagen guardada en el mapa.
  // A diferencia de las formas anteriores, fabric.Image.fromURL es asíncrono, por eso devuelve una Promise.
  addImageObject(src: string, config?: any): Promise<any> {
    return new Promise((resolve) => {
      fabric.Image.fromURL(src, (img: any) => {
        const imageConfig: any = {
          left: 100,
          top: 50,
          scaleX: 1,
          scaleY: 1,
          angle: 0,
          originX: 'center',
          originY: 'center',
          eventSeat: false,
          objectId: null,
          level: null
        };

        if (config) {
          imageConfig.left = config.left ?? imageConfig.left;
          imageConfig.top = config.top ?? imageConfig.top;
          imageConfig.scaleX = config.scaleX ?? imageConfig.scaleX;
          imageConfig.scaleY = config.scaleY ?? imageConfig.scaleY;
          imageConfig.angle = config.angle ?? imageConfig.angle;
          imageConfig.objectId = config.objectId || null;
          const imageConfigLevel = config.level;
          imageConfig.level = (imageConfigLevel != null && typeof imageConfigLevel === 'object')
            ? (imageConfigLevel.id ?? null)
            : (imageConfigLevel != null ? imageConfigLevel : null);
        }

        img.set(imageConfig);
        resolve(img);
      });
    });
  }

  // Método para recrear un SVG del mapa como objeto vectorial del canvas.
  // A diferencia de addImageObject, que lo cargaría en un <img> y por tanto lo rasterizaría a su
  // tamaño intrínseco (al hacer zoom el navegador reescala ese bitmap y se pixela como un JPG),
  // aquí se parsea el markup y se agrupan los elementos vectoriales: fabric los redibuja desde
  // los paths en cada render, así que se mantienen nítidos a cualquier nivel de zoom.
  addSvgObject(svgMarkup: string, config?: any): Promise<any> {
    return new Promise((resolve) => {
      // Un SVG mal formado hace que el parser de fabric lance; se resuelve a null para que el
      // llamador lo trate como "no se ha podido añadir" en vez de romper el pintado del mapa.
      try {
        fabric.loadSVGFromString(svgMarkup, (objects: any, options: any) => {
          const parsed = (objects || []).filter((obj: any) => !!obj);
          if (!parsed.length) {
            resolve(null);
            return;
          }

          // Quitar los recortes inútiles ANTES de agrupar/pintar: con ellos puestos, el primer
          // render del grupo cuelga el navegador (ver dropRedundantClipPaths).
          this.dropRedundantClipPaths(parsed);

          // Un SVG muy complejo (un plano exportado desde Illustrator puede traer miles de paths)
          // sin caché se repintaría entero en cada render de fabric —y fabric renderiza también al
          // mover el ratón—, dejando el mapa inutilizable. A partir de cierto número de elementos
          // se deja la caché activada: fabric la regenera al cambiar el zoom, así que se sigue
          // viendo nítido en los niveles de zoom normales.
          const complexSvg = parsed.length > this.SVG_UNCACHED_ELEMENTS_LIMIT;

          // Si el grupo entero se cachea, la caché de cada hijo no aporta nada y sí cuesta: fabric
          // se quedaría con un canvas offscreen por cada uno de los miles de paths (cientos de MB
          // de memoria gráfica retenida). Se desactiva antes de crear el grupo.
          if (complexSvg) {
            parsed.forEach((obj: any) => { obj.objectCaching = false; });
          }

          const group = fabric.util.groupSVGElements(parsed, options);

          const svgConfig: any = {
            left: 100,
            top: 50,
            scaleX: 1,
            scaleY: 1,
            angle: 0,
            originX: 'center',
            originY: 'center',
            eventSeat: false,
            // Marca para distinguir este grupo de las secciones/escenarios, que también son grupos.
            isSvgImage: true,
            // Un SVG normal va sin caché: así se repinta desde los vectores en cada render y no
            // se reescala un bitmap al ampliar. Los complejos sí se cachean (ver complexSvg).
            objectCaching: complexSvg,
            objectId: null,
            level: null
          };

          if (config) {
            svgConfig.left = config.left ?? svgConfig.left;
            svgConfig.top = config.top ?? svgConfig.top;
            svgConfig.scaleX = config.scaleX ?? svgConfig.scaleX;
            svgConfig.scaleY = config.scaleY ?? svgConfig.scaleY;
            svgConfig.angle = config.angle ?? svgConfig.angle;
            svgConfig.objectId = config.objectId || null;
            const svgConfigLevel = config.level;
            svgConfig.level = (svgConfigLevel != null && typeof svgConfigLevel === 'object')
              ? (svgConfigLevel.id ?? null)
              : (svgConfigLevel != null ? svgConfigLevel : null);
          }

          group.set(svgConfig);
          if (complexSvg) {
            this.enableAdaptiveCaching(group);
          }
          resolve(group);
        });
      } catch (error) {
        console.warn('[mapa] No se ha podido interpretar el SVG:', error);
        resolve(null);
      }
    });
  }

  // Caché adaptativa para los SVG complejos: cacheado mientras la caché salga nítida, y vectorial
  // (sin caché) cuando deje de serlo.
  //
  // Con `objectCaching` fabric pinta el grupo una vez en un canvas offscreen y reutiliza ese
  // bitmap, y lo regenera al cambiar el zoom, así que normalmente se ve nítido y va fluido. Pero
  // ese canvas tiene un tamaño máximo (fabric.perfLimitSizeTotal / maxCacheSideLimit): al ampliar
  // mucho, fabric ya no puede generarlo a la resolución que haría falta, lo recorta y estira el
  // bitmap => el dibujo se pixela.
  //
  // Justo ahí es cuando interesa lo contrario: repintar los miles de paths cuesta, pero a ese
  // zoom la mayor parte del dibujo se sale de la pantalla y el navegador descarta rápido lo que
  // no se ve. Así que se sustituye la decisión de fabric (`shouldCache`) por una que mira si la
  // caché va a salir recortada: si sale entera, se cachea; si no, se dibuja el vector.
  private enableAdaptiveCaching(group: any): void {
    const baseShouldCache = group.shouldCache;
    // Guarda por si una futura versión de fabric renombra estos métodos internos: sin ellos se
    // mantiene el comportamiento de siempre (cacheado).
    if (typeof baseShouldCache !== 'function'
      || typeof group._getCacheCanvasDimensions !== 'function'
      || typeof group._limitCacheSize !== 'function') {
      return;
    }
    group.shouldCache = function (this: any): boolean {
      // Deja que fabric decida primero (respeta objectCaching, grupos padre cacheados, sombras...).
      if (!baseShouldCache.call(this)) return false;
      // `capped` lo marca el propio fabric cuando ha tenido que recortar el tamaño de la caché,
      // que es exactamente el momento en que el bitmap dejaría de verse nítido.
      const dimensions = this._limitCacheSize(this._getCacheCanvasDimensions());
      if (!dimensions?.capped) return true;
      this.ownCaching = false;
      return false;
    };
  }

  // Elimina los `clipPath` que no recortan nada.
  //
  // Los SVG exportados por Illustrator/Inkscape (sobre todo los que vienen de un PDF) envuelven
  // todo el dibujo en un <g clip-path="url(#...)"> cuyo recorte es el rectángulo de la página.
  // fabric no conserva esa jerarquía: al aplanar el SVG le clona ese clipPath a CADA elemento
  // hijo. Y pintar un objeto con clipPath obliga a fabric a crear un canvas offscreen del tamaño
  // del recorte por objeto y por render, así que un plano con miles de paths intenta reservar
  // miles de canvas de megapíxeles y el navegador se queda colgado al pintar el mapa.
  //
  // Como ese recorte contiene por completo al objeto que recorta, quitarlo no cambia el resultado
  // visual. Solo se descarta cuando se puede demostrar: recortes rectangulares que envuelven al
  // objeto entero. Un recorte con forma real (una estrella, un texto), invertido, o que corta de
  // verdad al objeto, se conserva.
  private dropRedundantClipPaths(objects: any[]): void {
    let dropped = 0;
    objects.forEach((obj: any) => {
      if (!obj?.clipPath) return;
      if (!this.clipContainsObject(obj.clipPath, obj)) return;
      obj.clipPath = null;
      dropped++;
    });
    if (dropped) {
      console.info(`[mapa] SVG: descartados ${dropped} recortes que no recortaban nada.`);
    }
  }

  // ¿El recorte deja pasar el objeto entero? El clipPath se aplica en el espacio local del objeto
  // (centrado en él), así que se comparan ahí las 4 esquinas de la caja del objeto contra el
  // cuadrilátero del recorte.
  private clipContainsObject(clip: any, obj: any): boolean {
    // `inverted` recorta justo lo contrario (se ve lo de fuera) y un clipPath con otro clipPath
    // anidado recorta más de lo que dice su forma: en ambos casos hay que conservarlo.
    if (clip.inverted || clip.clipPath) return false;
    const clipCorners = this.getRectClipCorners(clip);
    if (!clipCorners) return false;

    // El trazo sobresale medio grosor por fuera del contorno.
    const halfWidth = (obj.width || 0) / 2 + (obj.strokeWidth || 0) / 2;
    const halfHeight = (obj.height || 0) / 2 + (obj.strokeWidth || 0) / 2;
    const objectCorners = [
      { x: -halfWidth, y: -halfHeight },
      { x: halfWidth, y: -halfHeight },
      { x: halfWidth, y: halfHeight },
      { x: -halfWidth, y: halfHeight }
    ];
    // Tolerancia relativa al tamaño del recorte: los exportadores dejan el borde de la página
    // justo sobre el contorno de los elementos que la tocan y no hay que contarlo como recorte.
    const clipSize = Math.max(
      Math.abs(clipCorners[2].x - clipCorners[0].x),
      Math.abs(clipCorners[2].y - clipCorners[0].y)
    );
    const tolerance = Math.max(1e-6, clipSize * 1e-4);
    return objectCorners.every((point) => this.isInsideConvexPolygon(point, clipCorners, tolerance));
  }

  // Esquinas del recorte, ya en el espacio local del objeto recortado, si su forma es un
  // rectángulo (girado o inclinado por la transformación, sigue siendo un cuadrilátero convexo).
  // null si no es un rectángulo y por tanto no se puede razonar sobre él con 4 puntos.
  private getRectClipCorners(clip: any): { x: number, y: number }[] | null {
    let points: { x: number, y: number }[] | null = null;

    if (clip.type === 'rect') {
      if (clip.rx || clip.ry) return null;
      const halfWidth = (clip.width || 0) / 2;
      const halfHeight = (clip.height || 0) / 2;
      points = [
        { x: -halfWidth, y: -halfHeight },
        { x: halfWidth, y: -halfHeight },
        { x: halfWidth, y: halfHeight },
        { x: -halfWidth, y: halfHeight }
      ];
    } else if (clip.type === 'polygon' || clip.type === 'polyline' || clip.type === 'path') {
      // Los puntos de path/polígono están en coordenadas de la forma: fabric la dibuja desplazada
      // -pathOffset para centrarla en su propio origen.
      const raw: { x: number, y: number }[] = [];
      if (clip.type === 'path') {
        for (const command of (clip.path || [])) {
          const letter = command?.[0];
          // Curvas o comandos raros => no es un rectángulo.
          if (letter === 'M' || letter === 'L') raw.push({ x: command[1], y: command[2] });
          else if (letter !== 'z' && letter !== 'Z') return null;
        }
      } else {
        (clip.points || []).forEach((p: any) => raw.push({ x: p.x, y: p.y }));
      }
      // Un rectángulo cerrado repite el primer punto al final.
      if (raw.length === 5 && Math.abs(raw[0].x - raw[4].x) <= 1e-3 && Math.abs(raw[0].y - raw[4].y) <= 1e-3) {
        raw.pop();
      }
      if (raw.length !== 4) return null;
      // Un rectángulo (antes de transformarlo) solo tiene dos x y dos y distintas.
      if (!this.hasTwoDistinctValues(raw.map((p) => p.x))) return null;
      if (!this.hasTwoDistinctValues(raw.map((p) => p.y))) return null;
      const offsetX = clip.pathOffset?.x || 0;
      const offsetY = clip.pathOffset?.y || 0;
      points = raw.map((p) => ({ x: p.x - offsetX, y: p.y - offsetY }));
    }

    if (!points) return null;
    // Del espacio de la forma al espacio local del objeto (posición, escala, giro e inclinación).
    const matrix = clip.calcTransformMatrix();
    return points.map((p) => fabric.util.transformPoint(new fabric.Point(p.x, p.y), matrix));
  }

  // Punto dentro de un cuadrilátero convexo: el punto queda al mismo lado de las 4 aristas.
  // La tolerancia se mide como distancia al lado, no como área, para que no dependa del tamaño.
  private isInsideConvexPolygon(point: { x: number, y: number }, polygon: { x: number, y: number }[], tolerance: number): boolean {
    let sign = 0;
    for (let i = 0; i < polygon.length; i++) {
      const current = polygon[i];
      const next = polygon[(i + 1) % polygon.length];
      const edgeX = next.x - current.x;
      const edgeY = next.y - current.y;
      const edgeLength = Math.hypot(edgeX, edgeY);
      if (!edgeLength) continue;
      // Distancia con signo del punto a la recta de la arista.
      const distance = (edgeX * (point.y - current.y) - edgeY * (point.x - current.x)) / edgeLength;
      if (distance > tolerance) {
        if (sign < 0) return false;
        sign = 1;
      } else if (distance < -tolerance) {
        if (sign > 0) return false;
        sign = -1;
      }
    }
    return true;
  }

  private hasTwoDistinctValues(values: number[]): boolean {
    const distinct: number[] = [];
    for (const value of values) {
      if (!Number.isFinite(value)) return false;
      if (!distinct.some((d) => Math.abs(d - value) <= 1e-3)) distinct.push(value);
      if (distinct.length > 2) return false;
    }
    return distinct.length === 2;
  }

  // Cambiar color del asiento
  changeSeatColor(elementSeat, color) {
    let elementStroke = elementSeat._objects.find(value => value.type != 'text');
    elementStroke?.set('fill', color);
    let rgb = this.hexToRgb(color);
    let textColor = (rgb.r * 0.299 + rgb.g * 0.587 + rgb.b * 0.114) > 186 ? '#000' : '#fff'
    let elementText = elementSeat._objects.find(value => value.type == 'text');
    elementText?.set('fill', textColor);
    return elementSeat;
  }

  hexToRgb(hex) {
    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  }

  addGroupInfo(canvas: any, icon: string, text: string, color: any, subtitle?: string, sectionNumber?: number): Promise<{ group: any, sectionId: number }> {
    const rectangle = this.createRect(color, 300, 150);
    const textComponent = this.createTextComponent(text, 18, -30);
    let elements = [rectangle, textComponent];

    if (subtitle) {
      const textCapacity = this.createTextComponent(subtitle, 16, 40);
      elements.push(textCapacity);
    }

    const currentSectionNumber = sectionNumber || 1;

    return new Promise((resolve) => {
      fabric.loadSVGFromURL(icon, function (objects, options) {
        var obj = fabric.util.groupSVGElements(objects, options);
        obj.set({ top: -5, scaleX: 0.05, scaleY: 0.05, originX: 'center', hasControls: false });
        elements.push(obj);

        var group = new fabric.Group(elements, {
          left: window.innerWidth * 0.2,
          top: 100,
          width: 300,
          height: 150,
          capacity: 50,
          lockRotation: true,
          eventSeat: true,
          isSection: true,
          section: { id: currentSectionNumber, name: 'Sección ' + currentSectionNumber },
        });

        group.subTargetCheck = true;
        group.perPixelTargetFind = true;
        group.lockMovementY = true;
        group.lockMovementX = true;
        group.lockRotation = true;
        group.lockScalingFlip = true;
        group.lockScalingY = true;
        group.lockScalingX = true;
        group.hoverCursor = 'pointer';
        group.hasBorders = false;
        group.hasControls = false;

        group.setControlVisible('mtr', false);
        canvas.add(group);
        canvas.renderAll();

        resolve({ group: group, sectionId: currentSectionNumber });
      });
    });
  }

  addGroupInfoWithSave(canvas: any, icon: string, text: string, color: any, subtitle: string, objectId: string, sectionNumber: number): Promise<{ group: any, sectionId: number, objectData: any }> {
    const rectangle = this.createRect(color, 300, 150);
    const textComponent = this.createTextComponent(text, 18, -30);
    let elements = [rectangle, textComponent];

    if (subtitle) {
      const textCapacity = this.createTextComponent(subtitle, 16, 40);
      elements.push(textCapacity);
    }

    return new Promise((resolve) => {
      fabric.loadSVGFromURL(icon, function (objects, options) {
        var obj = fabric.util.groupSVGElements(objects, options);
        obj.set({ top: -5, scaleX: 0.05, scaleY: 0.05, originX: 'center', hasControls: false });
        elements.push(obj);

        var group = new fabric.Group(elements, {
          left: window.innerWidth * 0.2,
          top: 100,
          width: 300,
          height: 150,
          capacity: 50,
          lockRotation: true,
          eventSeat: true,
          isSection: true,
          section: { id: sectionNumber, name: 'Sección ' + sectionNumber },
          objectId: objectId
        });

        group.subTargetCheck = true;
        group.perPixelTargetFind = true;
        group.lockMovementY = true;
        group.lockMovementX = true;
        group.lockRotation = true;
        group.lockScalingFlip = true;
        group.lockScalingY = true;
        group.lockScalingX = true;
        group.hoverCursor = 'pointer';
        group.hasBorders = false;
        group.hasControls = false;
        group.setControlVisible('mtr', false);
        canvas.add(group);
        canvas.renderAll();

        // Crear datos del objeto para guardar
        const objectData = {
          left: group.left,
          top: group.top,
          scaleX: group.scaleX,
          scaleY: group.scaleY,
          angle: group.angle || 0,
          capacity: 50,
          section: { id: sectionNumber, name: 'Sección ' + sectionNumber },
          objectId: objectId,
          text: text,
          color: color
        };

        resolve({ group: group, sectionId: sectionNumber, objectData: objectData });
      });
    });
  }

  // Counteracts group scale on text and SVG icon children so only the rect stretches
  applyScenaryChildScales(group: any) {
    if (!group?._objects) return;
    const scaleX = group.scaleX || 1;
    const scaleY = group.scaleY || 1;
    group._objects.forEach((child: any) => {
      if (child.type === 'rect') return;
      if (child._origScaleX == null) {
        child._origScaleX = child.scaleX || 1;
        child._origScaleY = child.scaleY || 1;
      }
      child.scaleX = child._origScaleX / scaleX;
      child.scaleY = child._origScaleY / scaleY;
    });
    group.dirty = true;
  }

  // ✅ Función unificada que reemplaza addScenary y recreateScenary
  createScenary(canvas: any, colorDefaultStage: any, screenElement: string, config?: any, fromEvent?: boolean, fromEdit?: boolean): Promise<{ group: any, objectData?: any }> {
    const rectangle = this.createRect(colorDefaultStage, config?.width || 700, config?.height || 250);
    const stageText = config?.text || 'Escenario';
    const textComponent = this.createTextComponent(stageText, 20, -30);
    let elements = [rectangle, textComponent];

    // Configuración por defecto para nuevo escenario
    const defaultConfig = {
      left: window.innerWidth * 0.2,
      top: 100,
      width: 700,
      height: 250,
      scaleX: 1,
      scaleY: 1,
      angle: 0,
      lockRotation: true,
      objectId: null
    };

    // Usar configuración pasada o valores por defecto
    const finalConfig = { ...defaultConfig, ...config };
    const self = this;

    return new Promise((resolve) => {
      fabric.loadSVGFromURL(screenElement, function (objects, options) {
        var obj = fabric.util.groupSVGElements(objects, options);
        obj.set({ top: -5, scaleX: 0.05, scaleY: 0.05, originX: 'center', hasControls: false });
        elements.push(obj);

        var group = new fabric.Group(elements, {
          left: finalConfig.left,
          top: finalConfig.top,
          width: finalConfig.width,
          height: finalConfig.height,
          scaleX: finalConfig.scaleX,
          scaleY: finalConfig.scaleY,
          angle: finalConfig.angle,
          lockRotation: fromEvent ? false : finalConfig.lockRotation,
          objectId: finalConfig.objectId,
          isScenary: true,
        });

        group.setControlVisible('mtr', false);
        self.applyScenaryChildScales(group);
        // Restore font size if saved
        if (finalConfig.fontSize) {
          const textObj = group._objects?.find((o: any) => o.type === 'text');
          if (textObj) textObj.set('fontSize', finalConfig.fontSize);
          // Reposicionar y escalar el icono en función del tamaño de fuente
          if (finalConfig.showIcon !== false) {
            const iconObj = group._objects?.find((o: any) => o.type !== 'rect' && o.type !== 'text');
            if (iconObj) {
              const iconTop = -30 + finalConfig.fontSize * 0.58 + 13;
              iconObj.set('top', iconTop);
              const baseScale = 0.05 * (finalConfig.fontSize / 20);
              const groupScaleX = group.scaleX || 1;
              const groupScaleY = group.scaleY || 1;
              iconObj._origScaleX = baseScale;
              iconObj._origScaleY = baseScale;
              iconObj.scaleX = baseScale / groupScaleX;
              iconObj.scaleY = baseScale / groupScaleY;
            }
          }
        }
        // Restore icon visibility if saved
        if (finalConfig.showIcon === false) {
          const iconObj = group._objects?.find((o: any) => o.type !== 'rect' && o.type !== 'text');
          if (iconObj) iconObj.set('visible', false);
          // Restore text vertical position when icon is hidden
          const textObj = group._objects?.find((o: any) => o.type === 'text');
          if (textObj) textObj.set('top', 0);
        }
        // Restore text color if saved
        if (finalConfig.textColor) {
          const textObj = group._objects?.find((o: any) => o.type === 'text');
          if (textObj) textObj.set('fill', finalConfig.textColor);
        }
        if (fromEvent && !fromEdit) {
          group.set({
            lockMovementX: true,
            lockMovementY: true,
            lockRotation: true,
            lockScalingX: true,
            lockScalingY: true,
            lockScalingFlip: true,
            lockUniScaling: true,
            hasControls: false,
            hasBorders: false,
            selectable: false,
            evented: false,
            hoverCursor: 'default',
            moveCursor: 'default'
          });
        } else if (fromEdit) {
          group.set({
            lockMovementX: false,
            lockMovementY: false,
            lockRotation: false,
            lockScalingX: false,
            lockScalingY: false,
            lockScalingFlip: false,
            lockUniScaling: false,
            hasControls: true,
            hasBorders: true,
            selectable: true,
            evented: true,
            hoverCursor: 'move',
            moveCursor: 'move'
          });
        } else {
          // View-only mode (not event, not edit) — lock
          group.set({
            selectable: false,
            evented: false,
            hoverCursor: 'default',
            moveCursor: 'default'
          });
        }

        canvas.add(group);
        canvas.renderAll();

        // ✅ Solo crear objectData si es un nuevo escenario (tiene objectId)
        let objectData = null;
        if (finalConfig.objectId) {
          objectData = {
            left: group.left,
            top: group.top,
            scaleX: group.scaleX,
            scaleY: group.scaleY,
            angle: group.angle || 0,
            width: finalConfig.width,
            height: finalConfig.height,
            objectId: finalConfig.objectId,
            text: stageText,
            fontSize: finalConfig.fontSize || 20,
            textColor: finalConfig.textColor || '#000000',
            fill: finalConfig.fill || colorDefaultStage,
            showIcon: finalConfig.showIcon !== false,
          };
        }

        resolve({ group: group, objectData: objectData });
      });
    });
  }

  limitObjectMovement(object: any) {
    object.subTargetCheck = true;
    object.perPixelTargetFind = true;
    object.lockMovementY = true;
    object.lockMovementX = true;
    object.lockRotation = true;
    object.lockScalingFlip = true;
    object.lockScalingY = true;
    object.lockScalingX = true;
    object.hoverCursor = 'default';
    object.hasBorders = false;
    object.hasControls = false;
    return object;
  }

}
